import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Radio, Textarea } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Campaign, CampaignProgress, Job } from "../api/types";
import { WEB_COST, gapGroups, nameOf, useGapWords } from "../lib/campaign";
import { PLANNING_RULE, enrichmentRule, expectedWords } from "../lib/enrichment";
import { markAsked, wasAsked } from "../lib/askedFiles";
import { dateAndTime, recentDate } from "../lib/format";
import { ActionTile, ActionTiles } from "../ui/ActionTile";
import { Button } from "../ui/Button";
import { Dialog, useConfirm } from "../ui/Dialog";
import { IconClipboard, IconClipboardCheck, IconContactCard, IconDownload, IconGlobe, IconNotes, IconRepeat } from "../ui/icons";
import { THIN_FILE, useNewestFile } from "./CampaignFile";
import { limitsWords, optionsArgs, optionsFrom, useCutPreview } from "./EnrichmentOptions";
import { EnrichmentRecords } from "./EnrichmentRecords";
import { Spinner } from "./Spinner";

/* The measurement's kinds as the records below name them. */
const RECORD_KIND: Record<string, "person" | "property" | "company"> = {
  people: "person", properties: "property", companies: "company",
};

/* A campaign's enrichment, top to bottom:
 *
 *   what it holds, now and added since tracking began, beside the areas for
 *     further enrichment (places more data would help, not faults a list
 *     has to clear before it is called): one section of data, read before
 *     the tools
 *   automated enrichment: the switch, the web and Apollo, what each is doing
 *   manual enrichment: what it is for, the request, research notes, the
 *     ZoomInfo file
 *   the records that need enrichment, each with what it is missing and one
 *     Edit (EnrichmentRecords.tsx). Each count in the areas for further
 *     enrichment opens them filtered to that gap.
 *
 * Nothing folds, because a setting in a fold gets missed. The automatic
 * switch is in Automated enrichment, the research notes in Manual
 * enrichment, and who to reach is criteria, on the Criteria tab and changed
 * through Edit criteria. docs/ux-decisions.md.
 *
 * Each section's actions are action tiles (src/ui/ActionTile.tsx): the
 * action and one sentence on what it does, in a row. What an action produced
 * (a run's progress, a file, a request's facts) is under or above the row,
 * never inside a tile. */
export function CampaignEnrichment({ token, c }: { token: string; c: Campaign }) {
  const name = c.name ?? "";
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [search, setSearch] = useSearchParams();
  /* A count in the areas for further enrichment opens the records below,
     filtered to that kind and gap, and brings them into view. */
  const openRecords = (kind: "person" | "property" | "company" | undefined, gapKey?: string) => {
    if (!kind) return;
    const next = new URLSearchParams(search);
    if (kind === "person") next.delete("r.kind"); else next.set("r.kind", kind);
    if (gapKey) next.set("r.gap", gapKey); else next.delete("r.gap");
    next.delete("r.page");
    next.delete("r.q");
    setSearch(next);
    window.requestAnimationFrame(() => document.getElementById("records")?.scrollIntoView({ block: "start" }));
  };
  const [said, setSaid] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [note, setNote] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const saved = optionsFrom(c);
  const [notes, setNotes] = useState(saved.researchNotes);
  /* The campaign's newest thin records file, from the engine (migration
     293), so the tile says it is being made, or when it was made, after the
     page is left and opened again, and the file downloads by itself in the
     tab that asked for it. */
  const thin = useNewestFile(token, name, THIN_FILE);
  const rule = enrichmentRule(c.stage);
  // In Planning, turning on web lookups and requesting manual enrichment
  // wait for Start enrichment (engine migration 259).
  const planning = c.stage === "draft" ? PLANNING_RULE : null;
  /* The dialog a spending tool opens first: who it is about and which gap,
     with the count before the press (api.campaign_people_count, engine
     migration 233). The list is the people above the campaign's line,
     everyone is every person connected to its properties, and companies
     are the companies with nobody to call, for a people search. */
  const [asking, setAsking] = useState<"apollo" | "thin" | "web" | null>(null);
  const [who, setWho] = useState<"list" | "everyone" | "companies">("list");
  const [gap, setGap] = useState<"mobile" | "mobile_or_email" | "anything">("mobile");
  const counted = useQuery({
    queryKey: ["campaign-people-count", name, who, asking === "web" ? "anything" : gap, asking],
    enabled: asking !== null,
    queryFn: () => callFunction<{ people: number; companies: number; taken: boolean }>(token, "campaign_people_count",
      { p_name: name, p_who: who, p_gap: asking === "web" ? "anything" : gap }),
  });
  const stops = useCutPreview(token, name, { perRole: saved.perRole, perCompany: saved.perCompany, reachFill: saved.reachFill });
  const atCut = (stops.data ?? []).find((s) => s.cut === (c.contact_cut_in_use ?? -1));

  const refresh = () => {
    for (const key of [["campaign", name], ["campaigns"], ["enrichment-requests"], ["campaign-jobs", name],
                       ["campaign-progress", name], ["campaign-history", name], ["campaign-file", name]]) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const progress = useQuery({
    queryKey: ["campaign-progress", name],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_progress")
        .select("baseline_at,measured_at,properties_added,people_change,mobiles_change,emails_change,gaps")
        .eq("criteria_set_name", name).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as CampaignProgress | null;
    },
  });
  const gapWords = useGapWords(token);

  const jobs = useQuery({
    queryKey: ["campaign-jobs", name],
    // Every 10 seconds while Apollo works for the campaign. The thin records
    // file is useNewestFile's, which follows it every 3.
    refetchInterval: (q) => ((q.state.data as Job[] | undefined) ?? []).some((j) => j.status === "queued" || j.status === "running") ? 10_000 : false,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("job")
        .select("id,kind,status,progress,result,error,queued_at,finished_at,artifact_key,expected_done_at")
        .in("kind", ["apollo_campaign"]).eq("params->>criteria_set", name)
        .order("queued_at", { ascending: false }).limit(6);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Job[];
    },
  });

  type Act = "request" | "start" | "complete" | "web" | "apollo" | "thin" | "auto_on" | "auto_off" | "notes";
  const act = useMutation({
    mutationFn: async (what: Act) => {
      switch (what) {
        case "request": return callFunction(token, "request_enrichment", { p_name: name });
        case "start": return callFunction(token, "start_enrichment", { p_name: name });
        case "complete": return callFunction(token, "complete_enrichment", { p_name: name, p_note: note.trim() || null });
        case "web": return callFunction(token, "queue_campaign_enrichment", { p_criteria_set: name, p_who: who === "companies" ? "list" : who });
        case "apollo": return callFunction(token, "request_apollo_enrichment", { p_name: name, p_who: who === "companies" ? "list" : who, p_gap: gap });
        case "thin": return callFunction(token, "build_thin_export", { p_name: name, p_who: who, p_gap: gap });
        case "auto_on": return callFunction(token, "set_enrichment_options", optionsArgs(name, { ...saved, autoEnrich: true }));
        case "auto_off": return callFunction(token, "set_enrichment_options", optionsArgs(name, { ...saved, autoEnrich: false }));
        case "notes": return callFunction(token, "set_enrichment_options", optionsArgs(name, { ...saved, researchNotes: notes }));
      }
    },
    onSuccess: (r, what) => {
      setSaid(null);
      if (what === "web" || what === "apollo" || what === "thin") setAsking(null);
      // This tab asked for it, so it downloads here by itself when made.
      if (what === "thin" && typeof r === "string") markAsked(r);
      if (what === "complete") { setCompleting(false); setNote(""); }
      if (what === "notes") setNotesOpen(false);
      refresh();
    },
    onError: (e: Error) => setSaid(e.message),
  });
  const busy = act.isPending;
  const pending = (what: Act) => act.isPending && act.variables === what;

  const p = progress.data;
  const gaps = gapWords.data ? gapGroups(p?.gaps, gapWords.data) : [];
  const request = c.manual_status;
  const open = request === "requested" || request === "in_progress";
  const thinRow = thin.row;
  const thinMaking = pending("thin") || thinRow?.kind === "export_running";
  /* The tile's sentence: the file being made, downloaded here, or failed,
     and otherwise when the newest file was made, which the History tab
     offers to download. A tile is one button, so a Download cannot go
     inside it. */
  const thinWords = thinMaking
    ? (wasAsked(thinRow?.job_id) || pending("thin") ? "Making the file. It downloads when it is ready." : "Making the file.")
    : thin.downloaded ? "Downloaded. Run it through ZoomInfo, then load the result on Import a file."
    : thin.fetchError ? thin.fetchError
    : thinRow?.failed ? `The file was not made. ${thinRow.note ?? ""}`.trim()
    : thinRow?.kind === "export" && thinRow.has_file
      ? <>Made {dateAndTime(thinRow.at)}{thinRow.by_name ? <> by {thinRow.by_name}</> : null}. The History tab has the file.</>
    : "A CSV with EVIE ids for ZoomInfo. Run it there, then load the result on Import a file.";
  const thinRefused = !thinMaking && (Boolean(thin.fetchError) || Boolean(thinRow?.failed));
  const apollo = (jobs.data ?? []).find((j) => j.kind === "apollo_campaign");
  const webRunning = (c.web_waiting ?? 0) > 0 && (c.web_members ?? 0) > 0;
  const searching = !webRunning && (c.enrich_job_status === "queued" || c.enrich_job_status === "running");
  /* The people to call with no phone or email yet: the enrichment work
     left, from the same count the export dialog shows
     (api.campaign_export_count, engine migration 239). */
  const exportCount = useQuery({
    queryKey: ["campaign-export-count", name, false],
    queryFn: () => callFunction<{ without_phone_or_email: number; taken: boolean }>(token, "campaign_export_count", { p_name: name }),
  });
  /* What the campaign holds now, from api.campaign, beside what enrichment
     added since the first measurement, from api.campaign_progress. Both
     read the newest measurement. A measure with no change counted has an
     empty cell rather than a zero. */
  const holds: { label: string; now: number | null | undefined; change: number | null | undefined }[] = [
    { label: "Properties", now: c.properties, change: p?.properties_added },
    { label: "Property owners", now: c.companies, change: undefined },
    { label: "People to call", now: c.people, change: p?.people_change },
    { label: "With a mobile", now: c.people_with_mobile, change: p?.mobiles_change },
    { label: "With an email", now: c.people_with_email, change: p?.emails_change },
    { label: "Without a phone or email", now: exportCount.data?.taken ? exportCount.data.without_phone_or_email : null, change: undefined },
    { label: "Everyone connected", now: c.people_everyone, change: undefined },
    { label: "Properties without a person to call", now: c.properties_with_nobody, change: undefined },
  ];
  /* How many records of each kind have at least one gap, for the head of
     its group. The people are those above the campaign's line, the same
     people the exports send out. */
  const gapTotals: Record<string, number | null | undefined> = {
    people: c.contacts_need_work, properties: c.properties_need_work, companies: c.owners_need_work,
  };
  /* The baseline is the first measurement. For a campaign made before the
     measurements began on 2026-10-02 that is later than the campaign itself,
     and the line says which. */
  const fromCreation = Boolean(p?.baseline_at && c.created_at
    && Math.abs(new Date(p.baseline_at).getTime() - new Date(c.created_at).getTime()) < 36 * 3600 * 1000);

  /* Turning the automatic switch on starts a run over the list, and a run
     of more than 500 records is confirmed first, because of what it costs.
     The engine decides when (api.campaign.web_needs_confirm, engine
     migrations 206 and 281). The web button opens the Who dialog instead,
     which says the same for a run that size. */
  const sweep = (what: "auto_on", label: string) => {
    if (!(c.web_needs_confirm && c.web_candidates)) { act.mutate(what); return; }
    confirm.ask({
      title: `Run web enrichment on ${c.web_candidates.toLocaleString()} records?`,
      says: WEB_COST,
      label,
      run: () => act.mutateAsync(what),
    });
  };
  const askAbout = (what: "apollo" | "thin" | "web") => {
    setWho("list");
    setGap(what === "thin" ? "anything" : "mobile");
    setAsking(what);
  };
  const askedCount = counted.data
    ? (who === "companies" ? counted.data.companies : counted.data.people)
    : null;
  /* Whether a web run of this many is large enough to say what it costs.
     The engine decides (api.web_sweep_size, engine migration 281). */
  const askedSize = useQuery({
    queryKey: ["web-sweep-size", askedCount],
    enabled: asking === "web" && askedCount !== null,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).rpc("web_sweep_size", { p_records: askedCount as number }).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as { needs_confirm: boolean | null }[])[0] ?? null;
    },
  });
  const askTitle = asking === "thin" ? "Download the thin records"
    : asking === "apollo" ? "Apollo enrichment"
    : "Web enrichment";
  const askLabel = askedCount === null ? (asking === "thin" ? "Download" : asking === "apollo" ? "Run Apollo enrichment" : "Run web enrichment")
    : who === "companies" ? `Download ${askedCount.toLocaleString()} ${askedCount === 1 ? "company" : "companies"}`
    : asking === "thin" ? `Download ${askedCount.toLocaleString()} people`
    : asking === "apollo" ? `Run Apollo enrichment on ${askedCount.toLocaleString()} people`
    : `Run web enrichment on ${askedCount.toLocaleString()} people`;

  return (
    <section className="rp-tabbody ce" aria-label="Enrichment">
      {/* What the campaign holds and where more data would help, as one
          section, before the tools that add to it. James, 2026-10-09: the
          counts and what was added since are one measurement, read as now
          and since, so they are one table. */}
      <div className="ce-block ce-data">
        <div>
          <h3>Current campaign contents {p?.measured_at ? <span className="muted ce-when">Measured {recentDate(p.measured_at)}</span> : null}</h3>
          <p className="ce-cut">
            Minimum fit {c.contact_cut_in_use ?? ""}{c.contact_cut === null || c.contact_cut === undefined ? ", the default" : ""}
            {atCut?.words ? <span className={atCut.in_red ? "refusal" : "muted"}>: {atCut.words}</span> : null}
            {" "}{limitsWords(c.contact_per_role, c.contact_per_company)}
            {" "}<Link to={`/campaigns/new?edit=${encodeURIComponent(name)}`}>Edit criteria</Link>
          </p>
          <table className="tight ce-holds">
            <thead>
              <tr>
                <th scope="col"><span className="sr-only">Measure</span></th>
                <th scope="col" className="num">Now</th>
                <th scope="col" className="num">
                  {p?.baseline_at ? (fromCreation ? "Added since creation" : <>Added since {recentDate(p.baseline_at)}</>) : "Added"}
                </th>
              </tr>
            </thead>
            <tbody>
              {holds.map((r) => (
                <tr key={r.label}>
                  <th scope="row">{r.label}</th>
                  <td className="num">{r.now === null || r.now === undefined ? <span className="muted">-</span> : r.now.toLocaleString()}</td>
                  <td className="num">{signed(r.change)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3>Areas for further enrichment</h3>
          {progress.isLoading || gapWords.isLoading ? <p className="muted"><Spinner />Loading.</p>
            : progress.isError ? <p className="refusal">{(progress.error as Error).message}</p>
            : gapWords.isError ? <p className="refusal">{(gapWords.error as Error).message}</p>
            : !p ? <p className="muted">Not measured yet.</p>
            : !gaps.length ? <p className="muted">Every record has what enrichment looks for.</p>
            : (
              <>
                <table className="tight ce-gaps">
                  {gaps.map((g) => (
                    <tbody key={g.kind}>
                      <tr className="ce-gap-group">
                        <th scope="rowgroup">
                          <Button kind="link" size="compact-sm" className="ce-gap-link" onClick={() => openRecords(RECORD_KIND[g.kind])}>
                            {g.label} with any gap
                          </Button>
                        </th>
                        <td className="num">{gapTotals[g.kind]?.toLocaleString() ?? ""}</td>
                      </tr>
                      {g.rows.map((r) => (
                        <tr key={r.key}>
                          <th scope="row">
                            <Button kind="link" size="compact-sm" className="ce-gap-link"
                                    onClick={() => openRecords(RECORD_KIND[g.kind], r.key.slice(g.kind.length + 1))}>
                              {r.words}
                            </Button>
                          </th>
                          <td className="num">{r.n.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </table>
              </>
            )}
        </div>
      </div>

      <div className="ce-block">
        <h3>Web and Apollo enrichment</h3>
        {rule ? <p className="refusal">{rule}</p> : planning && !c.auto_enrich ? <p className="muted">{planning}</p> : null}
        <ActionTiles label="Web and Apollo enrichment">
          <ActionTile
            icon={<IconRepeat />} title="Automatic web enrichment" on={Boolean(c.auto_enrich)}
            says={c.auto_enrich ? "EVIE runs web enrichment whenever records join this campaign."
              : "Turning it on runs web enrichment now, and again whenever records join."}
            onClick={() => c.auto_enrich ? act.mutate("auto_off") : sweep("auto_on", "Turn it on")}
            disabled={busy || (!c.auto_enrich && Boolean(rule || planning))} busy={pending("auto_on") || pending("auto_off")}
          />
          <ActionTile
            icon={<IconGlobe />} title="Run web enrichment"
            says={webRunning
              ? `${((c.web_members ?? 0) - (c.web_waiting ?? 0)).toLocaleString()} of ${(c.web_members ?? 0).toLocaleString()} records looked up, ${expectedWords(c.web_expected_done_at)}.`
              : searching ? "Searching the web for new records."
              : c.enrich_job_error ? `The last run failed. ${c.enrich_job_error}`
              : <>Finds changes, moves and new records, not phones.{c.web_candidates ? ` ${c.web_candidates.toLocaleString()} records have gaps it can fill.` : ""}</>}
            tone={!webRunning && !searching && c.enrich_job_error ? "refusal" : undefined}
            onClick={() => askAbout("web")}
            disabled={busy || Boolean(rule)} busy={pending("web") || webRunning || searching}
          />
          <ActionTile
            icon={<IconContactCard />} title="Run Apollo enrichment"
            says={apollo?.status === "running" ? `Apollo enrichment: ${apollo.progress ?? "starting"}, ${expectedWords(apollo.expected_done_at)}.`
              : apollo?.status === "queued" ? "Apollo enrichment is waiting in the queue."
              : apollo?.status === "failed" ? `The last request failed. ${apollo.error ?? ""}`.trim()
              : "Fills contact details, and asks for phones where no good mobile is known. 250 a request."}
            tone={apollo?.status === "failed" ? "refusal" : undefined}
            onClick={() => askAbout("apollo")}
            disabled={busy || Boolean(rule)} busy={pending("apollo") || apollo?.status === "running" || apollo?.status === "queued"}
          />
        </ActionTiles>
        {c.enrich_batch ? (
          <p className="ce-result"><Link to={`/enrichment?batch=${encodeURIComponent(c.enrich_batch)}`}>What web enrichment found</Link></p>
        ) : null}
      </div>

      <div className="ce-block">
        <h3>Manual enrichment</h3>
        <p className="ce-intro">
          Manual enrichment finds more records for this campaign, through CoStar, ZoomInfo and other sources that can't be automated yet.
        </p>
        {planning && (!request || request === "complete") ? <p className="muted">{planning}</p> : null}
        {request || saved.researchNotes ? (
          <dl className="ce-request-facts">
            {request ? (
              <>
                <dt>Requested</dt>
                <dd>{recentDate(c.manual_requested_at)}, by {nameOf(c.manual_requested_by)}</dd>
                <dt>Status</dt>
                <dd>
                  {request === "requested" ? "Waiting to be taken"
                    : request === "in_progress" ? <>In progress with {nameOf(c.manual_assigned_to)} since {recentDate(c.manual_started_at)}</>
                    : <>Completed {recentDate(c.manual_completed_at)}, by {nameOf(c.manual_completed_by)}</>}
                  {open ? (
                    <>{" · "}<Button kind="link" size="compact-sm" disabled={busy} onClick={() => confirm.ask({
                      title: "Withdraw the request for manual enrichment?",
                      says: "It comes off the manual enrichment queue on the Enrichment page. The request stays in this campaign's history.",
                      label: "Withdraw the request", danger: true,
                      run: () => callFunction(token, "withdraw_enrichment", { p_name: name }).then(refresh),
                    })}>Withdraw manual enrichment request</Button></>
                  ) : null}
                </dd>
                {request === "complete" && c.manual_note ? (<><dt>Found</dt><dd>{c.manual_note}</dd></>) : null}
              </>
            ) : null}
            {saved.researchNotes ? (<><dt>Research notes</dt><dd className="ce-notes-text">{saved.researchNotes}</dd></>) : null}
          </dl>
        ) : null}

        <ActionTiles label="Manual enrichment">
          {!request || request === "complete" ? (
            <ActionTile
              icon={<IconClipboard />}
              title={request ? "Request manual enrichment again" : "Request manual enrichment"}
              says={request ? "Adds this campaign back to the manual enrichment queue on the Enrichment page."
                : "Adds this campaign to the manual enrichment queue on the Enrichment page."}
              onClick={() => act.mutate("request")} disabled={busy || Boolean(rule || planning)} busy={pending("request")}
            />
          ) : request === "requested" ? (
            <ActionTile
              icon={<IconClipboard />} title="Take the request"
              says="Marks it in progress with you, so the team can see it is being worked."
              onClick={() => act.mutate("start")} disabled={busy} busy={pending("start")}
            />
          ) : (
            <ActionTile
              icon={<IconClipboardCheck />} title="Mark manual enrichment complete"
              says="Say what you found. A campaign at Enriching moves to Ready to call."
              onClick={() => { setNote(""); setCompleting(true); }} disabled={busy}
            />
          )}
          <ActionTile
            icon={<IconNotes />} title={saved.researchNotes ? "Edit the research notes" : "Add research notes"}
            says="Tell whoever pulls from CoStar or ZoomInfo which submarkets, types or splits to use."
            onClick={() => { setNotes(saved.researchNotes); setNotesOpen(true); }} disabled={busy}
          />
          <ActionTile
            icon={<IconDownload />} title="Download the thin records"
            says={thinWords}
            tone={thinRefused ? "refusal" : undefined}
            onClick={() => askAbout("thin")} disabled={busy} busy={thinMaking}
          />
        </ActionTiles>
      </div>

      {said ? <p className="refusal" role="alert">{said}</p> : null}

      <EnrichmentRecords token={token} c={c} />

      <Dialog opened={asking !== null} title={askTitle} onClose={() => setAsking(null)} busy={busy}
              disabled={askedCount === null || askedCount === 0}
              action={{ label: askLabel, onPress: () => { if (asking) act.mutate(asking); } }}>
        <Radio.Group label="Who" value={who} onChange={(v) => setWho(v as typeof who)} className="cx-group">
          <Radio value="list" className="cx-choice" label="The campaign's list"
                 description="The people above the line at each property." />
          <Radio value="everyone" className="cx-choice" label="Everyone connected"
                 description="Every person at every company with a role on the properties, above the line or not." />
          {asking === "thin" ? (
            <Radio value="companies" className="cx-choice" label="Companies of properties without a person to call"
                   description="For a people search in ZoomInfo or Apollo. The file has the companies and their websites." />
          ) : null}
        </Radio.Group>
        {asking !== "web" && who !== "companies" ? (
          <Radio.Group label="Missing" value={gap} onChange={(v) => setGap(v as typeof gap)} className="cx-group">
            <Radio value="mobile" className="cx-choice" label="A mobile" />
            <Radio value="mobile_or_email" className="cx-choice" label="A mobile or an email" />
            <Radio value="anything" className="cx-choice" label="Anything" description="A mobile, an email, a title or a LinkedIn profile." />
          </Radio.Group>
        ) : null}
        <p className="muted cx-note">
          {counted.isError ? <span className="refusal">{(counted.error as Error).message}</span>
            : counted.data && !counted.data.taken ? <span className="refusal">No people chosen for this list yet. Update the list first.</span>
            : askedCount === null ? <Spinner />
            : who === "companies" ? `${askedCount.toLocaleString()} ${askedCount === 1 ? "company" : "companies"} with a buying role on properties without a person to call.`
            : asking === "web" ? `${askedCount.toLocaleString()} people with a gap. The web finds changes, moves and new records, not phones.`
            : `${askedCount.toLocaleString()} people.`}
        </p>
        {asking === "web" && askedSize.isError
          ? <p className="refusal cx-note">{(askedSize.error as Error).message}</p>
          : asking === "web" && askedSize.data?.needs_confirm ? <p className="cx-warn">{WEB_COST}</p> : null}
      </Dialog>
      <Dialog opened={completing} title="Mark manual enrichment complete" onClose={() => setCompleting(false)} busy={busy}
              action={{ label: "Mark complete", onPress: () => act.mutate("complete") }}>
        <Textarea label="What did you find?" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} data-autofocus />
      </Dialog>
      <Dialog opened={notesOpen} title={saved.researchNotes ? "Edit the research notes" : "Add research notes"}
              onClose={() => setNotesOpen(false)} busy={pending("notes")}
              action={{ label: "Save the notes", onPress: () => act.mutate("notes") }}>
        <Textarea label="Research notes"
                  description="For whoever pulls from CoStar, ZoomInfo or other sources: submarkets, secondary types, how to split a pull. Not a filter."
                  value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={4} data-autofocus />
      </Dialog>
      {confirm.dialog}
    </section>
  );
}

/** A change as a signed figure: +19, \u221212, 0. Empty when none was counted. */
function signed(n: number | null | undefined) {
  if (n === null || n === undefined) return null;
  if (n > 0) return `+${n.toLocaleString()}`;
  if (n < 0) return `\u2212${Math.abs(n).toLocaleString()}`;
  return <span className="muted">0</span>;
}
